<template>
    <PageSidebar>
        <template #sidebarTitle> {{ $t("coffee.menu.styles") }}</template>
        <template #sidebarMain>
            <div>
                <CardToggle :title="$t('coffee.styles.icon')">
                    <div class="space-y-5">
                        <FormFiles
                            v-model="files"
                            :title="$t('coffee.styles.icon')"
                            accept="image/*"
                            :labels="{
                                clickToUpload: $t('shared.uploader.click_to_upload'),
                                dragAndDrop: $t('shared.uploader.drag_and_drop'),
                                fileInfo: $t('shared.uploader.file_details'),
                            }"
                            :maxSize="10000"
                            :errorMessages="{
                                invalidSize: $t('shared.invalid.file_size_over'),
                                invalidType: $t('shared.invalid.file_type'),
                            }"
                            :single-file="true"
                            @fileSelected="uploadImg"
                            @fileRemoved="removeImg"
                        />
                    </div>
                </CardToggle>
                <c-themes
                    :styleTheme="coffee.styleTheme"
                    :theme="coffee.theme"
                    @updateStyle="updateStyle($event)"
                    @updateColors="updateColors($event)"
                    @createTheme="createThemeFromCoffee"
                />
                <CardToggle :title="$t('shared.general')">
                    <div class="space-y-5">
                        <FormInput
                            :title="$t('shared.sizes.logo')"
                            placeholder="48"
                            v-model="coffee.coffeeSize"
                        />
                        <FormInput
                            :title="$t('shared.sizes.text_title')"
                            placeholder="18"
                            v-model="coffee.titleSize"
                        />
                        <FormInput
                            :title="$t('shared.sizes.text_basic')"
                            placeholder="14"
                            v-model="coffee.textSize"
                        />
                        <FormSelect
                            v-if="googleFonts?.length"
                            v-model="coffee.fontFamily"
                            :label="$t('shared.font_family')"
                            :options="googleFonts"
                            :value="coffee.fontFamily"
                        />
                    </div>
                </CardToggle>
                <CardToggle :title="$t('shared.colors.title')">
                    <div class="space-y-5">
                        <FormInputColor
                            :title="$t('shared.colors.button_background')"
                            :model-value="coffee.mainColor"
                            @update:model-value="changeColor('mainColor', $event)"
                            :tooltip="$t('shared.select_color_info')"
                        />
                        <FormInputColor
                            :title="$t('shared.colors.button_text')"
                            v-model="coffee.buttonTextColor"
                            :tooltip="$t('shared.select_color_info')"
                        />
                        <FormInputColor
                            :title="$t('shared.colors.background')"
                            :model-value="coffee.backColor"
                            @update:model-value="changeColor('backColor', $event)"
                            :tooltip="$t('shared.select_color_info')"
                        />
                        <FormInputColor
                            :title="$t('shared.colors.text')"
                            :model-value="coffee.textColor"
                            @update:model-value="changeColor('textColor', $event)"
                            :tooltip="$t('shared.select_color_info')"
                        />
                        <FormInputColor
                            :title="$t('shared.colors.border')"
                            :model-value="coffee.borderColor"
                            @update:model-value="changeColor('borderColor', $event)"
                            :tooltip="$t('shared.select_color_info')"
                        />
                    </div>
                </CardToggle>
            </div>
        </template>
        <template #sidebarFooter>
            <cSaveButton @save="saveSnapshot" :disabled="!hasChanges" />
        </template>
    </PageSidebar>
</template>

<script>
import { PageSidebar } from "@easy/ui/page-components";
import { CardToggle } from "@easy/ui/cards";
import { FormInput, FormInputColor, FormSelect, FormFiles, makeFileModel } from "@easy/ui/forms";
import {
    updateCoffeeColors,
    updateCoffeeStyle,
    resetProjectTheme,
} from "@/shared/themes/updateTheme";
import { mapState, mapActions } from "vuex";
import cThemes from "@/shared/components/cThemes.vue";
import cSaveButton from "@/shared/components/cSaveButton.vue";

export default {
    name: "SidebarStyles",
    components: {
        CardToggle,
        FormInputColor,
        FormSelect,
        cThemes,
        PageSidebar,
        FormInput,
        cSaveButton,
        FormFiles,
    },
    data() {
        return {
            files: [],
        };
    },
    watch: {
        coffee: {
            handler(coffee, oldCoffee) {
                if (!oldCoffee?.logo && coffee?.logo) {
                    this.files = [
                        makeFileModel({
                            url: coffee.logo,
                        }),
                    ];
                }
            },
            immediate: true,
            deep: true,
        },
    },
    computed: {
        ...mapState({
            googleFonts: (f) => f.googleFonts.map((label) => ({ label, value: label })),
        }),
        ...mapState("coffee", ["coffee", "hasChanges"]),
    },
    async mounted() {
        await this.getFonts();
    },
    methods: {
        ...mapActions(["getFonts"]),
        ...mapActions("theme", ["createTheme"]),
        ...mapActions("coffee/projects", ["saveSnapshot"]),
        ...mapActions("coffee", ["readLogoFile"]),
        changeColor(name, color) {
            this.coffee[name] = color;
            resetProjectTheme({ project: this.coffee });
        },
        updateStyle(style) {
            updateCoffeeStyle({ coffee: this.coffee, style });
        },
        updateColors({ theme }) {
            updateCoffeeColors({ coffee: this.coffee, theme });
        },
        uploadImg(file) {
            this.readLogoFile({ file });
        },
        removeImg() {
            this.coffee.logo = "";
        },
        async createThemeFromCoffee(name) {
            const { backColor, borderColor, fontFamily: font, mainColor, textColor } = this.coffee;
            const newTheme = await this.createTheme({
                name,
                font,
                colors: {
                    background: backColor,
                    border: borderColor,
                    brand: mainColor,
                    text: textColor,
                },
            });
            this.coffee.theme = String(newTheme.id);
        },
    },
};
</script>
